// Minimal API wrapper for landing-page-v2.
// Mirrors the pattern in landing-page/src/services/api.ts: relative path by
// default, configurable via VITE_API_PATH / VITE_API_BASE_URL. Kept lean —
// only what v2 actually calls today.

const API_PATH = import.meta.env.VITE_API_PATH || '/api'
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || ''

const getApiUrl = (endpoint: string): string => {
  if (API_BASE_URL) return `${API_BASE_URL}${API_PATH}${endpoint}`
  return `${API_PATH}${endpoint}`
}

// Read the SSO id_token straight from localStorage. We deliberately do NOT
// import ./auth here — auth.ts imports this module, and going the other way
// would create a circular dependency. Keep the storage key in sync with auth.ts.
const getAuthToken = (): string | null => {
  try {
    const raw = localStorage.getItem('od-landing-user')
    if (!raw) return null
    return JSON.parse(raw)?.id_token || null
  } catch {
    return null
  }
}

// Error carrying the HTTP status so callers can branch (e.g. 401 → prompt login).
export class ApiError extends Error {
  status: number
  constructor(message: string, status: number) {
    super(message)
    this.name = 'ApiError'
    this.status = status
  }
}

const apiRequest = async <T>(
  endpoint: string,
  options: RequestInit = {},
  timeoutMs = 30000
): Promise<T> => {
  const controller = new AbortController()
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs)
  const token = getAuthToken()
  try {
    const response = await fetch(getApiUrl(endpoint), {
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...(options.headers as Record<string, string> | undefined),
      },
      ...options,
      signal: controller.signal,
    })
    clearTimeout(timeoutId)
    if (!response.ok) {
      const body = await response.json().catch(() => ({}))
      throw new ApiError(body?.error || body?.message || `HTTP ${response.status}`, response.status)
    }
    return response.json()
  } catch (err) {
    clearTimeout(timeoutId)
    if (err instanceof Error && err.name === 'AbortError') {
      throw new Error('Request timeout')
    }
    throw err
  }
}

export interface WaitlistRequest {
  email: string
  name?: string
  use_case?: string
  join_beta?: boolean
}

export interface WaitlistResponse {
  success: boolean
  message: string
  id?: string
}

export interface ChangelogChange {
  id: string
  type: string
  title: string
  description: string
}

export interface ChangelogRelease {
  version: string
  released_at: string
  badge?: string
  media_url?: string
  media_type?: 'image' | 'video' | string
  changes: ChangelogChange[]
}

export interface ChangelogResponse {
  releases: ChangelogRelease[]
}

export const waitlistApi = {
  join: (data: WaitlistRequest): Promise<WaitlistResponse> =>
    apiRequest('/v1/waitlist', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
}

export const changelogApi = {
  list: (): Promise<ChangelogResponse> => apiRequest('/v1/changelog'),
}

// ---- GitHub affiliate deploy pages ---------------------------------------

export interface GitHubAffiliateProject {
  id: string
  slug: string
  owner: string
  repo: string
  display_name: string
  repo_url: string
  branch?: string
  updated_at: string
}

export interface GitHubAffiliateProjectResponse {
  project: GitHubAffiliateProject
}

export interface GitHubAffiliateProjectsResponse {
  projects: GitHubAffiliateProject[]
}

export const githubAffiliateProjectApi = {
  get: (owner: string, repo: string): Promise<GitHubAffiliateProjectResponse> =>
    apiRequest(`/v1/github-affiliate-projects/${encodeURIComponent(owner)}/${encodeURIComponent(repo)}`),
  list: (): Promise<GitHubAffiliateProjectsResponse> => apiRequest('/v1/github-affiliate-projects'),
}

// ---- feature requests ----------------------------------------------------

export interface FeatureRequestSubmit {
  title: string
  description: string
  email?: string
}

export interface FeatureRequestSubmitResponse {
  success: boolean
  id?: string
  message?: string
  error?: string
}

export type FeatureRequestPhase = 'now' | 'next' | 'soon' | 'later'

export interface PublicFeatureRequest {
  id: string
  title: string
  description: string
  phase?: FeatureRequestPhase | ''
  upvotes: number
  approved_at: string
  has_voted: boolean
}

export interface ApprovedFeatureRequestsResponse {
  data: PublicFeatureRequest[]
}

export interface UpvoteResponse {
  upvotes: number
  has_voted: boolean
}

export const featureRequestApi = {
  submit: (data: FeatureRequestSubmit): Promise<FeatureRequestSubmitResponse> =>
    apiRequest('/v1/feature-requests', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  listApproved: (): Promise<ApprovedFeatureRequestsResponse> =>
    apiRequest('/v1/feature-requests/approved'),
  upvote: (id: string): Promise<UpvoteResponse> =>
    apiRequest(`/v1/feature-requests/${id}/upvote`, { method: 'POST' }),
  removeUpvote: (id: string): Promise<UpvoteResponse> =>
    apiRequest(`/v1/feature-requests/${id}/upvote`, { method: 'DELETE' }),
}

// ---- blog ----------------------------------------------------------------

export interface BlogPostSummary {
  slug: string
  title: string
  description: string
  category: string
  tags: string[]
  read_time: string
  tldr: string
  cover_image_url?: string
  published_at: string
  comment_count: number
}

export interface BlogPostDetail extends BlogPostSummary {
  body_markdown: string
  comments_enabled: boolean
}

export interface BlogComment {
  id: string
  author_name: string
  body: string
  upvotes: number
  created_at: string
  has_voted: boolean
}

export interface BlogPostsResponse {
  data: BlogPostSummary[]
}

export interface BlogPostResponse {
  data: BlogPostDetail
}

export interface BlogCommentsResponse {
  data: BlogComment[]
  comments_enabled: boolean
}

export interface BlogCommentResponse {
  data: BlogComment
}

export const blogApi = {
  listPosts: (search?: string): Promise<BlogPostsResponse> =>
    apiRequest(`/v1/blog/posts${search ? `?search=${encodeURIComponent(search)}` : ''}`),
  getPost: (slug: string): Promise<BlogPostResponse> =>
    apiRequest(`/v1/blog/posts/${encodeURIComponent(slug)}`),
  listComments: (slug: string): Promise<BlogCommentsResponse> =>
    apiRequest(`/v1/blog/posts/${encodeURIComponent(slug)}/comments`),
  addComment: (
    slug: string,
    data: { author_name: string; body: string },
  ): Promise<BlogCommentResponse> =>
    apiRequest(`/v1/blog/posts/${encodeURIComponent(slug)}/comments`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  upvoteComment: (id: string): Promise<UpvoteResponse> =>
    apiRequest(`/v1/blog/comments/${id}/upvote`, { method: 'POST' }),
  removeUpvoteComment: (id: string): Promise<UpvoteResponse> =>
    apiRequest(`/v1/blog/comments/${id}/upvote`, { method: 'DELETE' }),
}

// ---- events --------------------------------------------------------------

export interface EventSummary {
  slug: string
  name: string
  description: string
  location: string
  cover_image_url?: string
  starts_at?: string
  ends_at?: string
  timezone?: string
  work_count: number
}

export interface EventDetail extends EventSummary {
  agenda_markdown: string
  meet_url?: string
  discord_url?: string
  wechat_qr_url?: string
  submissions_enabled: boolean
  private_submissions_enabled: boolean
}

export interface EventWork {
  id: string
  name: string
  link: string
  description: string
  author_name: string
  upvotes: number
  created_at: string
  has_voted: boolean
}

export interface EventsResponse {
  data: EventSummary[]
}

export interface EventResponse {
  data: EventDetail
}

export interface EventWorksResponse {
  data: EventWork[]
  submissions_enabled: boolean
}

export interface EventWorkResponse {
  data: EventWork
}

// Auth — exchanges a Casdoor access_token for the TokenDance account (creating
// the DB user on first sign-in). Shares the same backend endpoint the dashboard
// uses; see services/auth.ts for the full SSO flow.
export const authApi = {
  exchangeToken: (accessToken: string): Promise<{ user?: Record<string, unknown> } & Record<string, unknown>> =>
    apiRequest('/v1/auth/token', {
      method: 'POST',
      body: JSON.stringify({ access_token: accessToken }),
    }),
}

export const eventsApi = {
  listEvents: (search?: string): Promise<EventsResponse> =>
    apiRequest(`/v1/events${search ? `?search=${encodeURIComponent(search)}` : ''}`),
  getEvent: (slug: string): Promise<EventResponse> =>
    apiRequest(`/v1/events/${encodeURIComponent(slug)}`),
  listWorks: (slug: string): Promise<EventWorksResponse> =>
    apiRequest(`/v1/events/${encodeURIComponent(slug)}/works`),
  submitWork: (
    slug: string,
    data: { name: string; link: string; description: string; author_name: string; email: string },
  ): Promise<EventWorkResponse> =>
    apiRequest(`/v1/events/${encodeURIComponent(slug)}/works`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  upvoteWork: (id: string): Promise<UpvoteResponse> =>
    apiRequest(`/v1/event-works/${id}/upvote`, { method: 'POST' }),
  removeUpvoteWork: (id: string): Promise<UpvoteResponse> =>
    apiRequest(`/v1/event-works/${id}/upvote`, { method: 'DELETE' }),
  // Private submission: multipart (images + optional message/contact). Sent as
  // FormData so the browser sets the multipart boundary itself. Returns nothing
  // meaningful — the submission is private by design.
  submitPrivate: async (
    slug: string,
    data: { message: string; author_name: string; email: string; images: File[] },
  ): Promise<void> => {
    const form = new FormData()
    form.append('message', data.message)
    form.append('author_name', data.author_name)
    form.append('email', data.email)
    data.images.forEach((file) => form.append('images', file))
    const res = await fetch(getApiUrl(`/v1/events/${encodeURIComponent(slug)}/submissions`), {
      method: 'POST',
      credentials: 'include',
      body: form,
    })
    if (!res.ok) {
      const body = await res.json().catch(() => ({}))
      throw new Error(body?.error || body?.message || `HTTP ${res.status}`)
    }
  },
}

// ---- templates -----------------------------------------------------------

export interface TemplateSummaryDTO {
  slug: string
  name: string
  category: string
  one_liner: string
  live_demo_url: string
  preview_image?: string
  preview_image_2x?: string
  og_image?: string
  preview_alt?: string
  sort_order: number
  date?: string
  updated?: string
}

export interface TemplateDetailDTO extends TemplateSummaryDTO {
  description: string
  prompt: string
  builds: string[]
  stack: string[]
  needs_database: boolean
  follow_up_prompts: string[]
  allow_comments: boolean
  date?: string
  updated?: string
}

export interface TemplatesResponse {
  data: TemplateSummaryDTO[]
}

export interface TemplateResponse {
  data: TemplateDetailDTO
}

export interface TemplateComment {
  id: string
  author_name: string
  body: string
  upvotes: number
  created_at: string
  has_voted: boolean
}

export interface TemplateCommentsResponse {
  data: TemplateComment[]
  allow_comments: boolean
}

export interface TemplateCommentResponse {
  data: TemplateComment
}

export const templatesApi = {
  list: (): Promise<TemplatesResponse> => apiRequest('/v1/templates'),
  get: (slug: string): Promise<TemplateResponse> =>
    apiRequest(`/v1/templates/${encodeURIComponent(slug)}`),
  listComments: (slug: string): Promise<TemplateCommentsResponse> =>
    apiRequest(`/v1/templates/${encodeURIComponent(slug)}/comments`),
  addComment: (
    slug: string,
    data: { author_name: string; body: string },
  ): Promise<TemplateCommentResponse> =>
    apiRequest(`/v1/templates/${encodeURIComponent(slug)}/comments`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  upvoteComment: (id: string): Promise<UpvoteResponse> =>
    apiRequest(`/v1/template-comments/${id}/upvote`, { method: 'POST' }),
  removeUpvoteComment: (id: string): Promise<UpvoteResponse> =>
    apiRequest(`/v1/template-comments/${id}/upvote`, { method: 'DELETE' }),
}

export const api = {
  waitlist: waitlistApi,
  changelog: changelogApi,
  githubAffiliateProject: githubAffiliateProjectApi,
  featureRequest: featureRequestApi,
  blog: blogApi,
  events: eventsApi,
  templates: templatesApi,
}
